import {
  listEventAdminAssignments,
  listEventAssignmentAdminCandidates,
  replaceEventAdminAssignments,
  selfAssignEvents,
} from '../api/taskEvents';

export const getEventUuid = (event) =>
  event?.event_uuid || event?.eventUuid || event?.uuid || event?.id || null;

export const getAdminId = (auth) =>
  auth?.userId ||
  auth?.user?.userId ||
  auth?.user?.admin_user_id ||
  auth?.user?.id ||
  auth?.adminUser?.id ||
  auth?.admin?.id ||
  auth?.data?.userId ||
  auth?.data?.id ||
  null;

export const isAdminAssignedToEvent = (event, adminId) => {
  if (!event || !adminId) return false;
  const assignees = getEventAssignees(event);
  return assignees.some((assignee) => {
    const id =
      assignee?.admin_user_id ??
      assignee?.adminUserId ??
      assignee?.id ??
      assignee?.user_id ??
      null;
    return id != null && Number(id) === Number(adminId);
  });
};

const parseAssigneeList = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const hasOwn = (value, key) =>
  value != null && Object.prototype.hasOwnProperty.call(value, key);

export const getEventAssignees = (event) => {
  if (hasOwn(event, 'assigned_admins')) return parseAssigneeList(event.assigned_admins);
  if (hasOwn(event, 'assignedAdmins')) return parseAssigneeList(event.assignedAdmins);
  if (hasOwn(event, 'assignments')) return parseAssigneeList(event.assignments);
  return [];
};

export const getCurrentAdminAssignmentStatus = (event, adminId) => {
  if (!event || !adminId) return 'unknown';
  if (typeof event?.is_assigned_to_me === 'boolean') {
    return event.is_assigned_to_me ? 'assigned' : 'unassigned';
  }
  if (typeof event?.isAssignedToMe === 'boolean') {
    return event.isAssignedToMe ? 'assigned' : 'unassigned';
  }
  const hasAssignmentPayload =
    hasOwn(event, 'assigned_admins') ||
    hasOwn(event, 'assignedAdmins') ||
    hasOwn(event, 'assignments');
  if (!hasAssignmentPayload) return 'unknown';
  return isAdminAssignedToEvent(event, adminId) ? 'assigned' : 'unassigned';
};

export const assignCurrentAdminToEvents = async (eventUuids) => {
  const ids = [...new Set(
    (Array.isArray(eventUuids) ? eventUuids : [eventUuids])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
  )];
  if (!ids.length) return { assignedEventUuids: [], assignmentsByEventUuid: {} };
  return selfAssignEvents({ eventUuids: ids });
};

export const fetchEventAssignments = async (eventUuid) => {
  return listEventAdminAssignments(eventUuid);
};

export const resolveUnassignedEventUuids = async (
  events,
  adminId,
  { fetchAssignments = fetchEventAssignments, concurrency = 4 } = {}
) => {
  if (!adminId) return [];
  const eventByUuid = new Map();
  (Array.isArray(events) ? events : [events]).forEach((event) => {
    const eventUuid = getEventUuid(event);
    if (!eventUuid) return;
    const key = String(eventUuid);
    if (!eventByUuid.has(key)) eventByUuid.set(key, event);
  });

  const candidates = [...eventByUuid.entries()].filter(
    ([, event]) => getCurrentAdminAssignmentStatus(event, adminId) !== 'assigned'
  );
  const unassigned = [];
  const batchSize = Math.max(1, Math.min(4, Number(concurrency) || 4));

  for (let index = 0; index < candidates.length; index += batchSize) {
    const batch = candidates.slice(index, index + batchSize);
    const results = await Promise.all(
      batch.map(async ([eventUuid]) => {
        try {
          const canonical = await fetchAssignments(eventUuid);
          return getCurrentAdminAssignmentStatus(canonical, adminId) === 'unassigned'
            ? eventUuid
            : '';
        } catch (error) {
          console.warn('[eventAssignments] assignment status check failed', error);
          return '';
        }
      })
    );
    unassigned.push(...results.filter(Boolean));
  }

  return unassigned;
};

export const fetchEventAssignmentAdminCandidates = async () => {
  return listEventAssignmentAdminCandidates();
};

export const updateEventAssignments = async (eventUuid, adminUserIds) => {
  return replaceEventAdminAssignments(eventUuid, { adminUserIds });
};
